import { useState, useEffect } from 'react';
import { X, Link2, Copy, Check, Users, Eye, BookOpen, ShoppingCart, Shield, RefreshCw, Share2, Mail, MessageSquare, Phone, Send } from 'lucide-react';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';

export default function FamilyShareModal({ isOpen, onClose, storyId, storyName: initialStoryName }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [portal, setPortal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [storyName, setStoryName] = useState(initialStoryName || '');
  
  // Share form state
  const [showShareForm, setShowShareForm] = useState(null); // 'email' or 'sms' or null
  const [shareRecipientName, setShareRecipientName] = useState('');
  const [shareRecipientContact, setShareRecipientContact] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareError, setShareError] = useState(null);
  
  // Permission toggles
  const [permissions, setPermissions] = useState({
    viewResponses: true,
    viewSummaries: true,
    viewBookPreview: true,
    canOrderBook: true
  });
  
  // Optional PIN
  const [usePin, setUsePin] = useState(false);
  const [pin, setPin] = useState('');

  useEffect(() => {
    if (isOpen && storyId) {
      loadExistingPortal();
    }
  }, [isOpen, storyId]);

  // Update storyName when prop changes
  useEffect(() => {
    if (initialStoryName) {
      setStoryName(initialStoryName);
    }
  }, [initialStoryName]);

  const loadExistingPortal = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const storyDoc = await getDoc(doc(db, 'lifeStories', storyId));
      if (storyDoc.exists()) {
        const storyData = storyDoc.data();
        
        // Always get name from story data as fallback
        const fetchedName = storyData.preferredName || storyData.residentName || storyData.subjectName || '';
        if (fetchedName && !initialStoryName) {
          setStoryName(fetchedName);
        }
        
        // Load portal data if exists
        if (storyData.familyPortal) {
          const portalData = storyData.familyPortal;
          setPortal(portalData);
          setPermissions(portalData.permissions || permissions);
          setUsePin(!!portalData.hasPin);
        }
      }
    } catch (err) {
      console.error('Error loading portal:', err);
    }
    
    setLoading(false);
  };

  const generatePortalLink = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const createFamilyPortal = httpsCallable(functions, 'createFamilyPortal');
      const result = await createFamilyPortal({
        storyId,
        permissions,
        pin: usePin && pin.length === 4 ? pin : null
      });
      
      setPortal(result.data.portal);
      setCopied(false);
    } catch (err) {
      console.error('Error generating portal:', err);
      setError('Failed to generate family portal link. Please try again.');
    }
    
    setGenerating(false);
  };

  const regenerateLink = async () => {
    if (!confirm('This will invalidate the old link. Anyone with the old link will no longer have access. Continue?')) {
      return;
    }
    
    // Delete old portal document first
    if (portal?.token) {
      try {
        await deleteDoc(doc(db, 'familyPortals', portal.token));
      } catch (err) {
        console.error('Error deleting old portal:', err);
      }
    }
    
    await generatePortalLink();
  };

  const updatePermissions = async (newPermissions) => {
    setPermissions(newPermissions);
    
    if (portal?.token) {
      try {
        // Update BOTH the story document AND the familyPortals document
        await Promise.all([
          updateDoc(doc(db, 'lifeStories', storyId), {
            'familyPortal.permissions': newPermissions,
            'familyPortal.updatedAt': serverTimestamp()
          }),
          updateDoc(doc(db, 'familyPortals', portal.token), {
            'permissions': newPermissions,
            'updatedAt': serverTimestamp()
          })
        ]);
      } catch (err) {
        console.error('Error updating permissions:', err);
      }
    }
  };

  const copyLink = async () => {
    if (!portal?.url) return;
    
    try {
      await navigator.clipboard.writeText(portal.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const openShareForm = (method) => {
    setShowShareForm(method);
    setShareRecipientName('');
    setShareRecipientContact('');
    setSmsConsent(false);
    setShareError(null);
    setShareSuccess(false);
  };

  const closeShareForm = () => {
    setShowShareForm(null);
    setShareRecipientName('');
    setShareRecipientContact('');
    setSmsConsent(false);
    setShareError(null);
    setShareSuccess(false);
  };

  const handleSendShare = async (e) => {
    e.preventDefault();
    
    if (!shareRecipientName.trim()) {
      setShareError('Please enter the recipient\'s name');
      return;
    }
    
    if (!shareRecipientContact.trim()) {
      setShareError(`Please enter ${showShareForm === 'email' ? 'an email address' : 'a phone number'}`);
      return;
    }
    
    if (showShareForm === 'email' && !shareRecipientContact.includes('@')) {
      setShareError('Please enter a valid email address');
      return;
    }
    
    if (showShareForm === 'sms' && !smsConsent) {
      setShareError('Please confirm consent before sending');
      return;
    }
    
    setSending(true);
    setShareError(null);
    
    try {
      const sendFamilyPortalShare = httpsCallable(functions, 'sendFamilyPortalShare');
      await sendFamilyPortalShare({
        storyId,
        residentName: storyName,
        recipientName: shareRecipientName,
        recipientContact: shareRecipientContact,
        contactMethod: showShareForm,
        portalUrl: portal.url
      });
      
      setShareSuccess(true);
      setTimeout(() => {
        closeShareForm();
      }, 2000);
    } catch (err) {
      console.error('Error sending share:', err);
      setShareError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const shareViaNative = async () => {
    if (!portal?.url) return;
    
    const message = `View and contribute to ${storyName}'s Life Story: ${portal.url}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${storyName}'s Life Story`,
          text: message,
          url: portal.url
        });
      } catch (err) {
        // User cancelled or share failed
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-quiltt-purple" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Family Portal</h2>
              <p className="text-sm text-gray-500">Share {storyName}'s story with family</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-gray-500">Loading...</p>
            </div>
          ) : (
            <>
              {/* Permissions */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Family members can:</h3>
                <div className="space-y-2">
                  <PermissionToggle
                    icon={Eye}
                    label="View all recorded responses"
                    checked={permissions.viewResponses}
                    onChange={(checked) => updatePermissions({ ...permissions, viewResponses: checked })}
                  />
                  <PermissionToggle
                    icon={BookOpen}
                    label="View AI summaries & Quiltt Card"
                    checked={permissions.viewSummaries}
                    onChange={(checked) => updatePermissions({ ...permissions, viewSummaries: checked })}
                  />
                  <PermissionToggle
                    icon={Eye}
                    label="Preview book before ordering"
                    checked={permissions.viewBookPreview}
                    onChange={(checked) => updatePermissions({ ...permissions, viewBookPreview: checked })}
                  />
                  <PermissionToggle
                    icon={ShoppingCart}
                    label="Order and pay for books"
                    checked={permissions.canOrderBook}
                    onChange={(checked) => updatePermissions({ ...permissions, canOrderBook: checked })}
                  />
                </div>
              </div>

              {/* Optional PIN */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usePin}
                    onChange={(e) => setUsePin(e.target.checked)}
                    className="w-4 h-4 text-quiltt-purple rounded"
                  />
                  <Shield className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">Require PIN for extra security</span>
                </label>
                
                {usePin && (
                  <div className="mt-3 ml-7">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                      placeholder="4-digit PIN"
                      className="w-24 px-3 py-2 border rounded-lg text-center tracking-widest font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">Share this PIN separately with family</p>
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Generate or show link */}
              {!portal ? (
                <button
                  onClick={generatePortalLink}
                  disabled={generating || (usePin && pin.length !== 4)}
                  className="w-full py-3 bg-quiltt-purple hover:bg-purple-700 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {generating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-5 h-5" />
                      Generate Family Portal Link
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-4">
                  {/* Link display */}
                  <div className="p-4 bg-purple-50 rounded-xl">
                    <p className="text-xs text-purple-600 font-medium mb-2">Family Portal Link</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={portal.url}
                        className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-gray-700 truncate"
                      />
                      <button
                        onClick={copyLink}
                        className="p-2 bg-quiltt-purple hover:bg-purple-700 text-white rounded-lg transition-colors"
                      >
                        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                    {copied && (
                      <p className="text-xs text-green-600 mt-2">Copied to clipboard!</p>
                    )}
                  </div>

                  {/* Share buttons */}
                  {!showShareForm ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openShareForm('email')}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        Email
                      </button>
                      <button
                        onClick={() => openShareForm('sms')}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        Text
                      </button>
                      {navigator.share && (
                        <button
                          onClick={shareViaNative}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                        >
                          <Share2 className="w-4 h-4" />
                          Share
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Share Form */
                    <form onSubmit={handleSendShare} className="space-y-4 p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">
                          {showShareForm === 'email' ? '📧 Send via Email' : '📱 Send via Text'}
                        </h4>
                        <button
                          type="button"
                          onClick={closeShareForm}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      {shareSuccess ? (
                        <div className="text-center py-4">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Check className="w-6 h-6 text-green-600" />
                          </div>
                          <p className="text-green-600 font-medium">
                            {showShareForm === 'email' ? 'Email sent!' : 'Text sent!'}
                          </p>
                        </div>
                      ) : (
                        <>
                          {shareError && (
                            <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{shareError}</p>
                          )}
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Recipient's Name
                            </label>
                            <input
                              type="text"
                              value={shareRecipientName}
                              onChange={(e) => setShareRecipientName(e.target.value)}
                              placeholder="e.g., Sarah Johnson"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent outline-none"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {showShareForm === 'email' ? 'Email Address' : 'Phone Number'}
                            </label>
                            <input
                              type={showShareForm === 'email' ? 'email' : 'tel'}
                              value={shareRecipientContact}
                              onChange={(e) => setShareRecipientContact(e.target.value)}
                              placeholder={showShareForm === 'email' ? 'email@example.com' : '(555) 123-4567'}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent outline-none"
                            />
                          </div>
                          
                          {/* SMS Consent Checkbox */}
                          {showShareForm === 'sms' && (
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={smsConsent}
                                onChange={(e) => setSmsConsent(e.target.checked)}
                                className="mt-1 w-4 h-4 text-quiltt-purple border-gray-300 rounded focus:ring-quiltt-purple"
                              />
                              <span className="text-sm text-gray-600">
                                I confirm that <strong>{shareRecipientName || 'this person'}</strong> has given consent to receive a text message with the family portal link.
                                <span className="text-gray-500 block mt-1">Standard message rates may apply. They can reply STOP to opt out.</span>
                              </span>
                            </label>
                          )}
                          
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={closeShareForm}
                              className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={sending || (showShareForm === 'sms' && !smsConsent)}
                              className="flex-1 flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                            >
                              {sending ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4" />
                                  Send
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </form>
                  )}

                  {/* Regenerate link */}
                  {!showShareForm && (
                    <button
                      onClick={regenerateLink}
                      className="w-full flex items-center justify-center gap-2 py-2 text-gray-500 hover:text-gray-700 text-sm"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Generate new link (invalidates old link)
                    </button>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>No login required.</strong> Anyone with this link can access the family portal. 
                  Share it with family members via text, email, or any messaging app.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PermissionToggle({ icon: Icon, label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 text-quiltt-purple rounded"
      />
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}
